import { useState, useRef, useEffect } from "react";

// ─── Lightweight markdown → HTML renderer ────────────────────────────────────
function renderMarkdown(text) {
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Code blocks
  html = html.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) =>
    `<pre><code>${code.trim()}</code></pre>`
  );

  // Inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Headers
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Links
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Bare URLs
  html = html.replace(
    /(?<!["\'>])(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Horizontal rule
  html = html.replace(/^---+$/gm, "<hr/>");

  // Unordered lists
  html = html.replace(/^[\*\-] (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, "<oli>$1</oli>");
  html = html.replace(/(<oli>[\s\S]*?<\/oli>\n?)+/g, (m) =>
    `<ol>${m.replace(/<\/?oli>/g, (t) => t.replace("oli", "li"))}</ol>`
  );

  // Paragraphs
  html = html
    .split(/\n{2,}/)
    .map((block) => {
      block = block.trim();
      if (!block) return "";
      if (/^<(h[1-6]|ul|ol|pre|hr)/.test(block)) return block;
      return `<p>${block.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");

  // Remove <br/> tags between list items
  html = html.replace(/<\/li>\s*(<br\/>\s*)*/g, '</li>');
  html = html.replace(/(<\/ul>|<\/ol>)\s*(<br\/>\s*)*/g, '$1');

  return html;
}


// ─── TuneCore Knowledge Base context ────────────────────────────────────────
// Built by Intuitio Labs · Knowledge Base sourced from Notion
// Notion DB: collection://edc42fb0-fa4b-43f9-b96c-1193de02318f
// ─────────────────────────────────────────────────────────────────────────────

const PROJECT_CONTEXT = `
You are the TuneCore Support Assistant, built by Intuitio Labs. Answer questions using ONLY the Knowledge Base excerpts embedded below. Do not use any general knowledge about TuneCore or music distribution.

---

## RULE 1 — STRICT GROUNDING
Every statement you make must come directly from the KB text below — closely paraphrased or summarised. If a fact, step, fee, timeline, form name, field name, policy, or number is not explicitly stated in the KB, do not include it in your answer.

## RULE 2 — HARD FALLBACK
If the KB does not contain the information needed to answer — fully or partially — respond with:
"Not mentioned in the provided Knowledge Base. For this, please check support.tunecore.com or contact TuneCore Artist Support directly."
For partial coverage: answer only what the KB covers, then use the fallback phrase for the rest.

## RULE 3 — NO INVENTED PROCEDURES
Do not provide step-by-step navigation, UI paths, or action sequences unless the KB explicitly lists those exact steps. If the KB says "contact Artist Support" but does not describe how, do not invent the how.

## RULE 4 — NO INVENTED NUMBERS OR POLICIES
Do not state any fee, commission rate, threshold, deadline, tax rule, or policy detail unless it appears verbatim (or near-verbatim) in the KB excerpt. When in doubt, omit the detail and use the fallback.

## RULE 5 — CONFLICT RESOLUTION
If two KB sections appear to contradict each other on the same fact, say: "There is some inconsistency in the Knowledge Base on this — please confirm the current policy at support.tunecore.com." Do not pick one silently.

## RULE 6 — SELF-CHECK BEFORE RESPONDING
Before sending your answer, mentally verify: does each bullet point or sentence trace back to a specific KB section below? If not, remove it.

## RULE 7 — NO SPECULATION
Do not use words like "likely," "probably," "should," "typically," or "generally" unless those exact qualifiers appear in the KB. If you are uncertain, use the hard fallback.

---

## STYLE
- Be concise and friendly
- Use bullet points for multi-step or multi-item answers
- Briefly explain music industry terms only when the KB itself explains them
- Do not pad answers with information the user did not ask for

CONTACT: For issues not resolved here, visit https://support.tunecore.com or contact TuneCore Artist Support directly.


== WHO IS TUNECORE ==

TuneCore is a music distribution and publishing administration company that empowers independent artists, bands, and labels to distribute music globally, collect royalties, and build their careers — while retaining full ownership and control of their music. TuneCore has paid out billions of dollars in royalties to independent artists.


== TUNECORE'S CORE SERVICES ==

1. MUSIC DISTRIBUTION
TuneCore distributes music to 150+ stores and streaming platforms worldwide, including Spotify, Apple Music, Amazon Music, YouTube Music, Tidal, Deezer, TikTok, Instagram/Facebook, Pandora, iHeartRadio, Napster, Gaana, Qobuz, Douyin, and many more.
- Artists keep 100% of their sales revenue. TuneCore charges a flat annual distribution fee per release — not a royalty cut.
- Artists retain full ownership and copyright of their music at all times.
- TuneCore distributes Singles and Albums (TuneCore's platform does not have a dedicated EP product type, but albums with fewer tracks may be classified as EPs by stores like iTunes and Spotify). Ringtones are also supported.

2. PUBLISHING ADMINISTRATION
TuneCore Publishing Administration helps songwriters/composers collect publishing royalties that their PRO (Performing Rights Organization) may not be fully collecting:
- Mechanical royalties (from streaming and downloads)
- Foreign performance royalties (from international PROs)
- Synchronization royalties (Film & TV licensing)
TuneCore Publishing Administration requires exclusive administration rights for the compositions you register. It does NOT affect your copyright ownership.

COMMISSION RATES (Publishing Administration):
- 20% commission on royalties collected from the exploitation and broadcast of your compositions (performance, mechanical, etc.)
- 50% commission on any earnings from Synchronization (audio-visual / Film & TV licenses)
TuneCore Publishing will pitch your compositions for Film & TV sync opportunities.

3. YOUTUBE CONTENT ID
TuneCore can register your sound recordings with YouTube Content ID, allowing you to earn revenue when your music is used in YouTube videos — even videos you didn't upload.
- Revenue varies by viewer territory, ad format, viewer device, and user experience.
- Not all views will generate revenue (YouTube does not place ads on every view).
- Artists can monetize, block, or track videos that use their recordings.
- Artists can opt out of YouTube monetization even while using TuneCore Publishing Administration.


== AUDIO FILE REQUIREMENTS ==

TuneCore recommends:
- WAV file, 24-bit (sample size), 192 kHz (sample rate), 1411 kbps (bit rate), stereo mode

TuneCore also accepts:
- WAV file, 16-bit (sample size), 44.1 kHz (sample rate), 1411 kbps (bit rate), stereo mode

Important: Do NOT embed metadata in your audio files. Stores do not accept embedded metadata — all metadata (album title, artist name, song name, etc.) must be entered manually in TuneCore. Uploading must be done from a computer — not a mobile device or tablet.


== COVER ART REQUIREMENTS ==

Your artwork MUST be:
- JPG, PNG, or GIF image file
- Smaller than 10MB
- Perfect square, at least 1600 × 1600 pixels (iTunes recommends 3000 × 3000), no larger than 3000 × 3000 pixels
- RGB Color mode (including black and white images)
- Resolution of at least 72 dpi (300 dpi is better)
- Only include text stating the artist name and release title (exactly as entered in TuneCore), OR image only with no text
- Images you own the rights to distribute

Do NOT include: extra text, URLs or email addresses, pricing info, references to digital/physical formats (e.g. "CD," "Digital Exclusive"), iTunes or store logos, blurry or cut-off artwork, or copyrighted images you don't own.

Note: On Breakout or Professional Unlimited plans, TuneCore can generate cover art for you.


== RELEASE TYPE DEFINITIONS ==

TuneCore's platform has Singles and Albums. How stores classify them:

SINGLES:
- iTunes: 1–3 tracks, total ≤ 30 minutes, each track < 10 minutes. iTunes auto-appends "- Single" to the title.
- Spotify: Under 30 minutes, 3 or fewer tracks.
- Tracks priced at $0.99 on iTunes if under 10 minutes. Tracks 10+ minutes are sold at album price.

ALBUMS:
- TuneCore platform: Any release with 2 or more tracks (up to 100 tracks, max 2.5 hours).
- iTunes: 7+ tracks, OR 1–6 tracks but over 30 minutes total.

EPs (no dedicated EP product in TuneCore — albums may be classified as EPs by stores):
- iTunes EP: 4–6 tracks under 30 minutes, OR 1–3 tracks where one is ≥ 10 minutes and total is < 30 minutes. iTunes auto-appends "- EP".
- Spotify EP: Under 30 minutes, 4–6 tracks.

LONG SINGLES: A single track longer than 10 minutes can be distributed as a long single.


== PAYMENT & ROYALTIES TIMELINE ==

Publishing Administration Payouts:
TuneCore pays out publishing royalties 45 days following the end of each calendar quarter:
- Q1 (Jan–Mar): payout mid-May
- Q2 (Apr–Jun): payout mid-August
- Q3 (Jul–Sep): payout mid-November
- Q4 (Oct–Dec): payout mid-February of the following year

First royalty payment timeline: KB articles are inconsistent — one article states 9–12 months (12–18 months for foreign societies), another states 3–6 months (12–18 months for foreign). TuneCore recommends checking with support for the current timeline. All agree on quarterly payouts after the first payment. This is standard for any publisher due to the time required to register songs at global societies and process payments.

TuneCore requires tax information for payment:
- U.S. artists: W-9
- International artists: W-8BEN


== CHANGES AFTER DISTRIBUTION ==

Once a release has been sent to stores, the following CANNOT be changed:
- Adding, deleting, or reordering songs
- Changing the UPC or ISRCs (these are unique, release-specific identifiers and can never be edited)
- Changing the Original Release Date

To make these changes, you must request a takedown of the current release and distribute a new version (this counts as a new release and may require a new fee, unless on an Unlimited Plan). The resubmission is treated as a new product by stores.

Changes that CAN be made after distribution (check TuneCore's platform for specifics): pricing, store selection, and certain metadata corrections.


== COVER SONGS, REMIXES, SAMPLES & INTERPOLATIONS ==

COVER SONGS:
- You own the recording (your version) but not the original composition. You must obtain a mechanical license from the original copyright holder before distributing.
- In the U.S., licenses can be obtained via The Harry Fox Agency or TuneLicensing (TuneCore's partner).
- Soundalike covers (recordings that closely mimic the original) are NOT accepted by iTunes and most stores — these will be hidden and TuneCore cannot reverse this.
- Cover song formatting: Credit original songwriters with the role "Songwriter." Do NOT reference the original artist in the Main/Featuring Artist fields or track title.
  CORRECT: "All Along the Watchtower" or "All Along the Watchtower (Cover)"
  INCORRECT: "All Along the Watchtower (Cover of Bob Dylan)"
- Note for Indian music: Under Indian copyright law, cover licenses cannot be granted until 5 years after the original recording was made. Explicit permission from copyright owners is required.
- TuneCore Publishing users: A mechanical license for a cover does NOT grant rights to register the work for publishing — you must be a credited writer of the underlying composition.

REMIXES, SAMPLES, MASHUPS, INTERPOLATIONS:
- Must have a Master Use license from the owner of the original recording. These cannot be issued by a third-party licensor.
- You cannot distribute any content through TuneCore if you do not have 100% of the rights to that content.


== ISRC & UPC ==

- TuneCore automatically provides UPC (release barcode) and ISRC codes (per-track identifiers) at no extra charge.
- Artists can supply their own UPC/ISRC if they already have them.
- UPC and ISRC codes cannot be changed after distribution — they are permanently tied to that release.


== HATE CONTENT POLICY ==

TuneCore does not distribute hate speech or illegal content. TuneCore will refuse or take down content that:
1. Harasses or denigrates an individual or group
2. Promotes racism, bigotry, hatred, or physical harm
3. Is abusive, defamatory, pornographic, threatening, or obscene
4. Promotes the commission of illegal acts

TuneCore can take down such content without prior notification at their full discretion.


== OTHER KEY FACTS ==

COPYRIGHT:
- Music is automatically copyrighted upon creation in a fixed medium (written down or recorded). Registration is NOT required before using TuneCore, but is recommended — it creates a public record and enables monetary damages in legal disputes. U.S. registration: copyright.gov.

RENEWALS:
- TuneCore distribution is on an annual subscription basis. Artists must renew each release annually to keep it live in stores.

ACCOUNT MANAGEMENT:
- Two-Factor Authentication (2FA) is supported.
- Contact info, passwords, and payment methods can be updated in account settings.
- TuneCore supports closing your account — check the KB for the process.

REPORTING:
- TuneCore provides Trends & Analytics reports in the dashboard.
- TuneCore does NOT report artist sales or account information to third parties (including Luminate/Nielsen Soundscan). Artists who want Nielsen registration must do so directly at luminate.com.

SPECIAL POLICIES:
- TuneCore does NOT distribute audiobooks (recorded readings of published texts) to iTunes or any digital stores — use audible.com for audiobooks. However, spoken word albums/EPs/singles (non-audiobook) CAN be distributed to all stores EXCEPT Spotify US.
- TuneCore DOES offer CD, DVD, and poster manufacturing via its Disc Duplication service (available through the TuneCore account).
- Cover songs require a mechanical license — TuneCore offers Cover Song Licensing powered by Royalty Solutions.
- Chinese text in metadata cannot be distributed to certain stores.
- Hebrew language releases cannot be distributed to iTunes.
- Peloton Music: TuneCore has a specific distribution relationship with Peloton.
- Labels can distribute music for multiple artists through TuneCore.
- Charity/non-profit music can be released through TuneCore.


== PUBLISHING CONCEPTS ==

WRITER'S SHARE VS PUBLISHER'S SHARE:
Every composition has two sets of rights: the writer's share and the publisher's share.
- Writer's share: belongs to the songwriter/composer. Always paid directly to the writer by the PRO.
- Publisher's share: the share that can be administered by a publishing company. TuneCore Publishing collects this on your behalf.
As a songwriter, you own BOTH shares. A Publishing Administration agreement does NOT transfer ownership — it only grants TuneCore the right to administer and collect the publisher's share. You remain the owner at all times.
Example: Finneas O'Connell (co-writer of Billie Eilish's "Bad Guy") signed an admin deal with Kobalt. Kobalt collects the publisher's share from ASCAP and thousands of sources worldwide, while ASCAP still pays the writer's share directly to Finneas.

TYPES OF SONGWRITER INCOME (4 categories):
1. Mechanical Royalties — from interactive/on-demand streaming, digital downloads, physical products (vinyl, CDs), ringtones.
2. Public Performance & Broadcast Royalties — from streaming, digital radio, terrestrial radio, TV, live concerts, bars, restaurants, gyms.
3. Synchronization (Sync) Licensing Fees — from TV, film, commercials, video games.
4. Print Royalties — from sheet music (online and physical), songbooks.

SHARES & CO-WRITING:
Your "share" is the percentage of the composition copyright you own. If you wrote a song entirely yourself, your share is 100%. With co-writers, you divide ownership by agreement — e.g., 50/50 between lyricist and composer, or equal splits among band members. A producer may ask for 10–50% in lieu of a fee. Always agree on shares in writing. TuneCore Artist Support can help update shares if they change.

LETTER OF DIRECTION (LOD):
An LOD is a formal notice sent to collection societies authorizing TuneCore to take over administration of your catalogue. It does NOT grant TuneCore ownership. An LOD is only required if you already have an existing publishing entity affiliated with a PRO. You'll sign it electronically during registration if applicable.

RETROACTIVE COLLECTION:
TuneCore can sometimes collect previously unclaimed royalties, but makes no guarantees — it depends on the rules at each collection society. Retroactive collection is typically possible for 2–3 years back. If you previously had a publishing deal, it may affect TuneCore's ability to collect retroactively. Unclaimed income typically shows up 2–3 quarters after joining. To maximize chances: submit all song info including co-writer names, shares, ISRC codes, and recording details.

WHAT TUNECORE PUBLISHING DOES NOT ADMINISTER:
TuneCore Publishing will NOT accept the following compositions (they don't qualify as musical works under U.S. copyright law):
- Sound effects / sound design (nature sounds, white noise, animal sounds, car horns, etc.)
- Ringtones — ONLY accepted if they contain an original music composition created by you
- Spoken word — accepted ONLY if backed by an original musical composition you created. No famous speeches in any form unless licensed.
- Royalty-free music — cannot submit royalty-free music as original work, or original music composed for royalty-free purposes
- Binaural / meditation / hypnosis / self-help sounds — no massage, tantric, or relaxation works
- Public domain works — unless it is a new, unique arrangement disclosed during signup
- Works wholly generated by AI technology — AI-generated compositions are NOT accepted

REFUND POLICY:
All sales are final. For questions, contact TuneCore support directly.


== PUBLISHING ADMINISTRATION — SETUP & MANAGEMENT ==

COST & FEES:
$75 one-time sign-up fee + 20% commission on royalties collected. 50% commission on Synchronization licenses.
- Sign up at tunecore.com or via your account under Publishing Administration → Register.

ADDING COMPOSITIONS:
You can add new compositions to your TuneCore Publishing account at any time — as many as you want. Login to your TuneCore account and select the compositions you wish to add, including your individual writer share. Compositions not distributed through TuneCore can also be added.

EXISTING PUBLISHING DEALS:
If some of your compositions are already represented by another publisher or publishing administration company, TuneCore cannot represent those specific compositions until that agreement expires. TuneCore can represent your other compositions in the meantime.

TERMINATING YOUR PUBLISHING AGREEMENT:
- Initial term: 1 year from date of payment
- After year 1: auto-renews quarterly at no additional cost
- To terminate: submit a request after the first year
- If request received 45+ days before quarter end → effective at end of current quarter
- If request received less than 45 days before quarter end → effective at end of following quarter
- Post-Term Collection rights: TuneCore retains the right to collect income generated during the term for 12 months after the termination date
- After termination: it is the client's (or new administrator's) responsibility to notify collection societies of the change

WHY YOU NEED PUBLISHING ADMINISTRATION EVEN IF YOU HAVE A PRO:
PROs (ASCAP, BMI, SESAC) collect ONLY performance royalties. They cannot legally collect mechanical royalties. Mechanical royalties from interactive streaming and digital downloads represent 50–71% of total publishing income and are NOT collected by PROs or included in TuneCore's distribution payments. A publishing administrator works in tandem with your PRO to collect all royalty types. Without both, you are leaving the majority of your publishing income uncollected.

WHAT IS A MUSIC PUBLISHING ADMINISTRATOR?
A music publishing administrator licenses, registers, and collects royalties on behalf of the music you write — an additional revenue source entirely separate from distribution royalties. Under an Administration Deal, you retain 100% ownership and control. TuneCore Publishing ensures you don't leave money on the table globally.

DOES TUNECORE PUBLISHING TAKE OWNERSHIP OF MY COPYRIGHTS?
No. TuneCore is granted exclusive administration rights only for the term of the agreement. You maintain 100% ownership and control of your songs and copyrights at all times.


== COMPOSITION VS SOUND RECORDING ==

A composition is the musical work created by the songwriter — the melody, lyrics, and song structure. A sound recording (also called a "master") is the recorded performance of that composition, including all instrumentation and vocals.
Example: Dolly Parton wrote the composition "I Will Always Love You." Whitney Houston recorded a version of it. Arista Records owns Whitney's sound recording; Dolly Parton (and her publisher) own the underlying composition. TuneCore Distribution handles sound recording royalties; TuneCore Publishing handles composition royalties.


== EXPLICIT & CLEAN TRACKS ==

EXPLICIT: Contains curse words, or language/art that is sexual, violent, or offensive in nature.
- If one track on an album is marked explicit, the entire album is marked explicit.
- If your release is actually explicit but not marked as such, it may be denied by the Content Review team or hidden in stores.
- Explicit content is NOT sold in these iTunes territories: Burkina Faso, India, Nepal, Uzbekistan.

CLEAN: A version that does NOT contain explicit content. Mark as "Clean" ONLY if an explicit version of the same track exists — this prevents customers from accidentally purchasing the explicit version.


== RELEASE ISSUES & TROUBLESHOOTING ==

RELEASE FLAGGED BY CONTENT REVIEW:
If your release status shows "Not Sent" or "Unreleased," or you see a red banner on login, your release was flagged by TuneCore's Content Review Team. You will receive an email from contentreview@tunecore.com explaining why and how to fix it. After making changes, reply to that email to request re-review. If you can't find the email, contact contentreview@tunecore.com with your UPC and release title. Response time: within 24 business hours.

MUSIC REMOVED FROM STORES:
Most likely cause: annual renewal fee was not paid. TuneCore sends multiple notifications before the renewal date and provides a grace period. Once removed, it is final and cannot be reversed — you must create a brand new release. The old UPC cannot be reused.

SINGLE SHOWING AS "VARIOUS ARTISTS" ON SPOTIFY:
Per Spotify's style guide, if 4 or more primary artists or remixers are credited on a release, Spotify replaces all names with "Various Artists." Solution: credit no more than 3 primary artists or remixers.


== YOUTUBE CONTENT ID — DETAILED ==

HOW CLAIMS WORK:
When you opt in for YouTube Content ID monetization, your audio is delivered to YouTube's Content ID database. YouTube scans all existing and future video uploads against this database. When a match is found, a "claim" is placed on that video — this is NOT a copyright strike and does NOT negatively affect the uploader's channel standing. It simply enables monetization so you (the rights holder) receive a share of the ad revenue generated by that video.



== ROYALTIES — FULL BREAKDOWN ==

WHAT TUNECORE PUBLISHING COLLECTS BEYOND YOUR PRO:
Your PRO only collects performance royalties. TuneCore Publishing collects everything else:

Mechanical Royalties (NOT collected by PROs):
- Interactive/on-demand streaming (Spotify, Apple Music, YouTube, Tidal, Deezer)
- Digital downloads (iTunes, Amazon)
- Physical products (vinyl, CDs, cassettes)
- Ringtones/ringbacks (AT&T, T-Mobile)
- Cover versions of your songs recorded by others
- Samples of your songs used in new recordings
- Karaoke recordings of your songs
- Greeting cards, on-demand jukeboxes (e.g. TouchTunes)

Print Royalties:
- Physical and digital sheet music
- Lyric reprints (physical liner notes, digital platforms like Spotify, MusixMatch, Instagram)
- Guitar tablature

Sync & Micro-Sync:
- TV shows, commercials, films, film trailers, TV promos
- Video games, mobile applications, DVD/Blu-Ray
- YouTube, TikTok, and other social platforms (micro-sync generates both mechanical AND performance royalties)

Performance Royalties (publisher's share — some PROs do not pass this through):
- Interactive streaming, AM/FM radio, internet radio, satellite radio
- TV broadcast royalties, restaurants, bars, gyms, retail outlets, live venues

STREAMING ROYALTIES EXPLAINED — HOW MANY STREAMS APPLY:
Interactive/On-Demand (Spotify, Apple Music, YouTube, Tidal):
→ 3 royalty streams: (1) Performance royalty → your PRO, (2) Mechanical streaming royalty → TuneCore Publishing, (3) Sound recording royalty → TuneCore Distribution

Non-Interactive/Internet Radio (Pandora, SiriusXM, Slacker):
→ 2 royalty streams: (1) Performance royalty for composition → your PRO, (2) Performance royalty for sound recording → SoundExchange (paid direct to artists)

SOUNDEXCHANGE vs TUNECORE PUBLISHING:
- TuneCore Publishing: collects royalties for the songwriter of the COMPOSITION
- SoundExchange: collects royalties for the SOUND RECORDING from non-interactive digital radio (satellite, internet radio). If you are a recording artist, affiliate directly with SoundExchange.

YOUTUBE CONTENT ID — COMPOSITION vs SOUND RECORDING:
YouTube Content ID only monetizes SOUND RECORDING rights (the audio). It does NOT monetize composition rights. To monetize your composition on YouTube, use TuneCore Publishing Administration.

YOUTUBE ACCRUED ROYALTIES:
TuneCore Publishing has entered the YouTube Accrued Royalties Payment Agreement on behalf of all publishing customers — you do NOT need to separately opt in. This agreement allows TuneCore to claim and distribute previously-unidentified or unclaimed composition royalties from YouTube.

RETROACTIVE YOUTUBE MONETIZATION:
YouTube does NOT allow retroactive payment for views that occurred before you opted into Content ID. Only future views after opting in are monetized.

COMPILATION TRACKS ON YOUTUBE:
Compilation licenses generally grant the right to distribute the licensed master but NOT the right to monetize on YouTube. Only the original producer of each song can claim and monetize it on YouTube.

YOUTUBE CONTENT ID REVENUE SPLIT:
No upfront cost to opt in. TuneCore keeps 20%, you keep 80% of all revenue. $0 annual subscription fee. Applies to YouTube Music, YouTube Content ID, CapCut, Facebook/Instagram/Reels, TikTok/TikTok Music/Resso.

CAN SOMEONE STOP ADS ON THEIR VIDEO?
No. Once a Content ID claim is placed on a video using your music, the video uploader cannot stop YouTube from placing ads or remove already-placed ads. The only way to avoid ads on a claimed video is to take the video down completely.


== DISTRIBUTION — DETAILED STORE & PRICING INFO ==

STORE PRICING:
iTunes albums: Number of tracks × $0.99 (max $9.99 for 10+ tracks, or if any track is 10+ minutes)
iTunes singles: $0.99 per track (tracks 10+ minutes default to $9.99)
Amazon: Wholesale price set at upload — cannot be changed after distribution
iTunes price changes: Can be made after distribution; album changes take 1–3 business days; track-level changes update every Friday (submit by Tuesday)
Music cannot be made available for free download — all releases on download stores must have a price.

SALES REPORTS & PAYMENT TIMING (Distribution):
- Stores report sales on a 2-month delay: sales from January appear in your account in March
- TuneCore posts sales reports every weekend for stores that reported that week
- iTunes typically reports on the first weekend of the month
- Spotify typically reports on the last weekend of the month
- You will NOT receive notifications when sales are posted — check your account starting 2 months after your music goes live
- No activity = no report for that month

TAX REPORTING:
- TuneCore will send a 1099 (US) or 1042 (international) for Publishing Administration royalties that meet IRS reporting thresholds in a given tax year

GENRE RESTRICTIONS:
- TuneCore uses genres common across all partner stores — not all genres are available
- You can select a primary and secondary genre
- iTunes may change: "Folk" → "Singer/Songwriter", "Inspirational" → "Christian/Gospel"
- Amazon may change: "Spoken Word" → "Miscellaneous/Poetry", "Singer/Songwriter" → "Rock/Singer/Songwriter", "Electronic" → "Dance & Electronic"
- Classical albums CANNOT be distributed to iTunes (TuneCore lacks the interface for required metadata like composers, movements, tempo)
- "Instrumental" genre cannot be sent to iTunes but can go to all other stores

TERRITORY RESTRICTIONS:
Artists can restrict distribution to specific countries/territories. Key store territories include global platforms (TikTok, Boomplay, JioSaavn, Gracenote, Shazam) and region-specific ones (Pandora = US only; iHeartRadio = US only; Amazon Music = select countries; Spotify, Apple Music, Deezer = 50–80+ countries each).

ALBUM ONLY TRACKS:
- iTunes: Only allowed if you have a written license from a third party prohibiting individual sale. Max 12.5% of tracks per album (e.g. 1 track on an 8–15 track album). Tracks under 10 minutes cannot be marked Album Only. Only distributable to iTunes and Amazon MP3 if any tracks are Album Only.
- Amazon Music: Contact TuneCore before distributing to request Album Only tracks.

SEARCH RESULT PLACEMENT:
Store search results are based on popularity (sales, click-throughs) and algorithmic relevance. TuneCore cannot manually control search ranking. Best approach: share your direct store link on social media to drive clicks and sales.

UPC & ISRC — HOW TO GET THEM:
TuneCore UPC: Generated automatically after Content Review approves your release (if you don't enter your own). Found on your Album Overview page after approval.
TuneCore ISRC: Generated automatically when you save a song during upload. Found under each track in your release overview.
Bringing your own: You can enter an existing UPC or ISRC during upload (pay-per-release or Professional Unlimited Plan required for custom UPC).
Applying for official codes: ISRCs via your local ISRC agency (IFPI); UPCs via your local GS1 office.

RENEWALS — FULL DETAILS:
- All releases are on 1-year, 2-year, or 5-year subscription plans
- Auto-renewal is ON by default — TuneCore charges your saved payment method automatically
- Email notification sent 28 days before renewal date
- If renewal fails: email reminders every 7 days for 35 days (42-day grace period total)
- After 42 days without payment: release is taken down permanently
- To renew manually: Account Settings → Subscriptions → "Pay now"
- Distribution credits cannot be used for renewals

CREDITING CONTRIBUTORS:
Roles available: Primary Artist, Featured Artist, Songwriter, Producer, Actor (soundtracks only), Composer (classical only), Lyricist (theatrical only), Remixer (remixes only)
Important: An artist can be credited as Primary OR Featured on a project — NOT BOTH. TuneCore cannot deliver releases where an artist is Primary on some tracks and Featured on others.
At least one Songwriter must be credited on every release.
Featuring artists must be added as contributors — not added to the song title.

MAKING CHANGES TO A LIVE RELEASE:
Locked (require full takedown + resubmit): track order, UPC, ISRC, original release date
Self-serviceable from your discography: TikTok Start Time, Songwriter credits, Language of Lyrics (not Chinese/Russian/Arabic/Hebrew/Bulgarian/Ukrainian), certain Contributors
Via TuneCore support (not guaranteed in all stores): other metadata changes (Spotify, iTunes, Amazon usually process within 2 weeks; other stores may take months or never update)
Takedown warning: all store reviews/comments and playlist appearances tied to the original UPC are permanently lost


== PUBLISHING — ADDITIONAL DETAILS ==

SYNC APPROVAL:
TuneCore's team negotiates the highest possible sync rate on your behalf (rate depends on budget, length of use, and terms). For contentious uses — political/religious advocacy, sexual content, alcohol products — TuneCore will seek your approval before granting a license. For sync licensing inquiries: licensing@tunecore.com

WHY EXCLUSIVE ADMIN RIGHTS ARE REQUIRED:
If you appoint more than one publishing administrator for the same compositions, there will be duplicate claims at rights societies, delaying or stopping royalty distributions for those works. Exclusivity ensures clean, efficient collection.

WHY EXCLUSIVE SYNC RIGHTS:
TuneCore holds exclusive sync rights for compositions to avoid marketplace confusion and maximize negotiating power. If you work with a licensing house for your sound recordings, TuneCore can still license the composition portion — have the interested party email licensing@tunecore.com

PRO AFFILIATION FEES:
US PROs (as writers): BMI = free, ASCAP = $50 application fee, SESAC = invite only
US PROs (as publishers): BMI = $250 non-refundable, ASCAP = $50 non-refundable, SESAC = invite only
Example international: PRS (UK) = £50 as writer; MCPS (UK) = £50 as writer, £400 as publisher
Important: TuneCore acts as your publisher through its affiliated entities (TuneCore Digital Music/BMI, TuneCore Publishing/ASCAP, TuneCore Songs/SESAC) — so you do NOT need to register your own publishing entity. At minimum, you should be affiliated as a writer with a PRO.
Mixed affiliations are NOT allowed (e.g. BMI as writer + ASCAP as publisher). There must be consistency.
International writers affiliated with a non-US PRO can choose BMI or ASCAP for US licensing (ASCAP is the default).

CAN I REGISTER UNRELEASED MUSIC?
Yes. Registering with TuneCore Publishing before release ensures royalties flow to you the moment you distribute. No royalties are received until the music is actually released and generating streams/sales.

COVERS AND PUBLISHING:
- You can only submit ORIGINAL compositions for publishing — not covers, not compositions you didn't write
- A mechanical license to distribute a cover does NOT grant publishing rights
- Co-writes are accepted — TuneCore collects your individual share; provide co-writer names and shares

EXISTING PUBLISHER CONFLICTS:
TuneCore cannot administer compositions already under an active publishing deal with another entity. They can represent all other compositions in your catalogue immediately.


== TUNECORE SOCIAL & TOOLS ==

TUNECORE SOCIAL:
Free for all TuneCore artists with an active distribution. An all-in-one social media management tool for musicians. Post to: Facebook Fan Page, Twitter, SoundCloud, Mixcloud. Analytics from: Facebook, Twitter, Instagram, SoundCloud, YouTube, Mixcloud. Can schedule posts in advance. Scheduled Posts and Media Uploads reset on the 1st of each month.

TUNECORE ACCELERATOR — ARTIST PITCH FORMS:
Available to Professional Unlimited Plan subscribers in: Australia, Indonesia, LATAM (Mexico, Brazil, Argentina, Puerto Rico, Colombia), Philippines, Singapore, Thailand, UK, US. Pitch your release to DSP playlists (TuneCore New Music Friday, and other discovery playlists). Pitch at least 4 weeks before release date. Requires a completed TuneCore Artist Profile for all primary artists. Playlist placement is NOT guaranteed.

COVER SONG LICENSING:
TuneCore CoverSong Licensing powered by Royalty Solutions (TuneLicensing) researches rights holders and secures mechanical license agreements for cover songs. Contact: support@tunelicensing.com (allow 1–3 business days).

PRE-ORDER (iTunes):
Benefits: market with a buy link before release, offer Instant Gratification tracks (immediately downloadable upon pre-order purchase), pre-order sales count toward iTunes chart position during the pre-order period, optional lower pre-order price that changes on release date, fans get automatic download on release day. Up to half the tracks can be Instant Gratification tracks (requires at least 4 tracks).


== STORE-SPECIFIC DETAILS ==

QOBUZ:
Hi-fi streaming and download service based in Paris. 60 million tracks in CD lossless quality; 2 million+ Hi-Res tracks. Available in: France, UK, Ireland, Germany, Austria, Belgium, Switzerland, Luxembourg, Netherlands, Spain, Italy.
⚠️ IMPORTANT: Qobuz only accepts high-resolution audio files — minimum 16-bit, 44 kHz in WAV, AIFF, or FLAC format. MP3s will NOT be accepted. Store Automator will only deliver existing releases to Qobuz if they meet this requirement.
Go-live time: within 5 business days after TuneCore review. Revenue: monthly, starting ~2 months after release goes live.

GAANA:
One of India's most popular music streaming apps, with 185+ million monthly active users. Features: playlists, radio, discovery portal, voice assistant for multilingual searches. Streams and downloads. Go-live time: ~5 business days after TuneCore review. Revenue: proportionate share of monthly subscription revenue.

DOUYIN / QISHUI YINYUE:
Chinese short-form video platform (like TikTok for China), with 600+ million daily users. Creators add music to short videos; viewers can stream full tracks and save to libraries. Go-live time: ~48 hours. Revenue: quarterly.

PELOTON MUSIC:
Discovery platform for new artists on Peloton fitness equipment. Available in: US, Canada, UK, Germany. Offers social media exposure through class content and member sharing. ⚠️ IMPORTANT: Content is screened and verified with MRI for 100% publishing rights — if the distributing artist does not have 100% publishing rights, the content will NOT be made active.

ITUNES POPULARITY BARS:
Popularity bars are relative only to other songs on the same release. They are based primarily on sales but also influenced by searches, clicks, and preview plays. A single purchased track on an album may show a full popularity bar even with minimal total sales — it's all relative within that release.


== ACCOUNT MANAGEMENT — HOW TO GUIDES ==

RESET PASSWORD:
Go to tunecore.com → Login → "I forgot my password" → enter your account email → follow instructions in email.
Password requirements: at least 8 characters, 1 uppercase letter, 1 number, 1 special character from: @ $ ! % * ? &

UPDATE CONTACT INFORMATION:
Account icon (top right) → Account Settings → select the tab to update → enter current password → Save Changes.
Note: Changing your contact info phone number does NOT change the phone number associated with Two-Factor Authentication (2FA).

TWO-FACTOR AUTHENTICATION (2FA):
Powered by Authy. Adds a unique code sent to your phone at login, withdrawal, or account changes. Can receive via SMS or phone call. Set up via Account Settings. Triggered when: logging in, requesting a withdrawal, changing email/password/bank accounts, or accessing from a new browser or device. If you receive an unexpected code, change your login credentials immediately. To disable: Account Settings → Two-Factor Authentication → Edit → Disable.

CLOSE YOUR ACCOUNT:
If you have made purchases: your account CANNOT be closed — it must stay open so you can access future funds (stores have 2-month delays and some report quarterly). If you have NOT made any purchases: contact TuneCore support from your registered email.

RENEWAL PAYMENT (MANUAL):
Account icon → Account Settings → Subscriptions tab → "Pay now" next to the release. Note: Distribution credits cannot be used to pay for renewals.

PROMO CODES:
At checkout, click "Enter Promo Code" on your product, enter the code, and click "Continue With Checkout." Note: Distribution credits are typically excluded from promo codes.

LABELS & MULTIPLE ARTISTS:
Labels can use TuneCore without a special label account. Manage multiple artists from one account. Sales reports can be filtered by release and artist. Withdraw to multiple US bank accounts, multiple PayPal accounts, or have checks mailed to different addresses. Each account has one login — individual artists cannot check their own sales without account access.

ARTIST PAGE LINKS (SPOTIFY & APPLE MUSIC):
Add your existing Spotify or Apple Music artist page link during upload to ensure new releases are mapped to the correct page.
Spotify: Desktop app → your artist page → three-dot icon → Share → Copy Link to Artist.
Apple Music: Music app → search your artist name → three-dot icon → Copy Link.
Paste the link (remove everything after the question mark) in the TuneCore upload flow when asked if you have existing music on that platform.

APPLE MUSIC FOR ARTISTS:
Claim and verify your Apple Music artist profile via your TuneCore Dashboard → "Apple Music For Artists" box. Provides analytics (who's streaming, location, milestones, download data), ability to upload a personalized artist photo, and access to the Apple Music for Artists iOS app.


== DISTRIBUTION — ADDITIONAL DETAILS ==

LONG SINGLES:
A single track that is longer than 10 minutes can be distributed as a single — as long as it is truly one continuous track. If the content has natural breaks and stops, it must be separated into tracks and distributed as an album. iTunes automatically marks tracks 10+ minutes as "album only" and prices them at $9.99. Other stores may price them at $0.99 — consider distributing long singles to iTunes and Amazon only to control pricing.

TERRITORY RESTRICTIONS:
Available on Pay-Per-Release model or Professional Unlimited plan only. Set during release creation under "Countries & Regions." Options: sell worldwide with some exclusions, OR sell in specific countries only. Territory restrictions are FINAL and cannot be reversed after distribution. Cannot have different restrictions per store — one set applies to all selected stores. To set restrictions on an already-distributed release, contact TuneCore support with your UPC and desired restrictions.

HEBREW LANGUAGE:
Hebrew language content is blocked from iTunes only (iTunes requires both Hebrew script and transliterated English, which TuneCore cannot deliver). Hebrew content CAN be distributed to all other stores.

CHINESE TEXT:
Chinese characters in artist name, release title, or track name fields are NOT supported by TuneCore's delivery systems — releases with Chinese text will be rejected at content review.

CHARITY / NON-PROFIT MUSIC:
TuneCore welcomes charity releases. There are no non-profit discounts — standard distribution fees apply. The account holder (charity organizer) is responsible for managing all tax and donation information.

AUDIOBOOKS / SPOKEN WORD:
- Audiobooks (recordings of published texts): TuneCore CANNOT distribute these to iTunes or any digital stores. Use audible.com (they have exclusive audiobook distribution rights).
- Spoken word albums/EPs/singles (non-audiobook): CAN be distributed to all stores EXCEPT Spotify US.

CD / DVD / POSTER MANUFACTURING:
TuneCore DOES offer Disc Duplication services for manufacturing CDs, DVDs, and posters. Access via your TuneCore account to get a quote and select features.

LUMINATE / NIELSEN SOUNDSCAN:
TuneCore does NOT report artist sales or account information to any third parties including Luminate (formerly Nielsen Soundscan). If you want to register with Nielsen/Luminate, visit their site directly.

LINKSHARE (TUNECORE SOCIAL PRO):
Creates one shareable link that directs fans to your music across all active streaming stores. Requires TuneCore Social Pro subscription + at least one live release. One link per release; links cannot be edited or deleted after creation. To create: TuneCore Social dashboard → linkShare → select release → choose stores → add description → select template → copy and share link.

YOUTUBE CREATOR MUSIC:
A YouTube feature allowing video creators to purchase sync licenses for your music. TuneCore is a partner. Available to US YouTube Partner Program creators. Licenses are non-exclusive, per-video, worldwide. Sync royalty rates apply. Does not replace Content ID income. Excluded: commercial brand channels, paid promotional videos, remixes, lyric videos, art tracks. You can remove your music from the library at any time (prior licenses remain valid). Cannot takedown a video with a valid license unless the creator violates license restrictions.

YOUTUBE VIDEO MONETIZATION EXCLUSIONS:
You cannot exclude specific videos from Content ID monetization — monetization applies to all videos that use a track. Exception: you can whitelist ONE specific YouTube channel. If you don't want a specific track monetized at all, you must remove it entirely from YouTube monetization.

PUBLISHING ADMINISTRATION — YOUTUBE OPT-OUT:
You CANNOT opt out of composition (micro-sync) monetization on YouTube within the Publishing Administration service — it is included by default. A YouTube channel must have 1,000 subscribers and 4,000 hours of watch time in the past 12 months to start generating royalties. Sound recording YouTube monetization (YTSR) is a separate product from Publishing Administration.

TUNECORE SYNC — FILM & TV PITCHING:
TuneCore Sync has in-house creative teams on both US coasts who actively pitch music to film and TV music supervisors. They respond to supervisor briefs, prepare music packages for productions, and do regular industry-wide blasts. For sync licensing inquiries: licensing@tunecore.com.

SAMPLES & INTERPOLATIONS FOR PUBLISHING:
Compositions containing samples/interpolations CAN be submitted for publishing IF you have the proper license:
- Mechanical license: for covering or re-recording someone else's work (obtain via tunelicense.com)
- Sample license: for using an actual recording excerpt (may need both master and composition clearance)
- Beat licenses (BeatStars, Airbit, Soundee): may be eligible — check if exclusive or non-exclusive; usage restrictions may apply
- Royalty-free loops (Splice, Logic, GarageBand): generally eligible — check platform terms
You CANNOT register a standalone loop or sample as an original song. The loop must be part of a new composition.



== TUNECORE PLANS & PRICING ==

PLAN TYPES:
TuneCore has two models:
1. Pay-Per-Release: Pay a flat fee per release per year (1, 2, or 5-year subscriptions). Each release renews individually.
2. Unlimited Distribution Plans: Single annual fee covers ALL releases in your account (past and future). Plan tiers include Rising Artist, Breakout Artist, and Professional.

Note: The New Artist Plan was DISCONTINUED on June 18, 2025. Artists who did not upgrade had releases taken down. To reinstate: upgrade to an Unlimited Plan, then contact Artist Support via Chat Support with the list of UPCs to restore.

UNLIMITED PLAN KEY FACTS:
- Once you switch to Unlimited, ALL releases (past and future) are covered under a single yearly fee
- All releases renew on the same date (not individually throughout the year)
- Once switched, you CANNOT go back to pay-per-release
- Only ONE plan allowed per account at a time
- Distribution credits are LOST when switching from pay-per-release to Unlimited
- You can upgrade to a higher tier anytime; prorated discount applied for time remaining on current plan
- You CANNOT downgrade without contacting Artist Support (they verify eligibility based on features used)
- Downgrade takes effect at next renewal date

PROFESSIONAL PLAN EXCLUSIVE FEATURES:
- Multiple primary artists on releases (first artist included; additional artists = $14.99/year each, prorated for mid-year additions)
- Custom Label Name
- Custom Recording Location
- Custom UPC (non-TuneCore generated)

BREAKOUT ARTIST + PROFESSIONAL PLAN FEATURES:
- Store Automator
- Daily Trend Reports
- Cover Art Creator

ARTIST NAME CASE-SENSITIVITY:
Artist names are case-sensitive. "Lil Shouty," "lil shouty," and "LIL SHOUTY" are treated as different artists and each may incur an additional artist fee on Professional Plans.

PRIORITY ARTIST:
Each Unlimited account has a Priority Artist. View/change via Account Settings (Person Icon → Account Settings → Priority Artist section).

UNLIMITED PLAN RENEWALS:
- Auto-renews annually; cannot turn off auto-renewal without canceling subscription entirely
- Email reminders: 28 days before and 2 days before renewal
- Grace period: 35 days (email every 7 days) after renewal date
- After 42 days without payment: account downgraded, all releases taken down
- To reinstate after grace period: contact support with list of UPCs
- To cancel: Account Settings → Subscriptions → Cancel Subscription (music stays live until cancellation date shown)

ADDING STORES AFTER DISTRIBUTION:
- Unlimited Plan users: Music → Discography → find release → "+Add Stores" (free); OR Music → Store Manager → select stores → Checkout
- Pay-Per-Release users: Cannot add stores to an existing release without taking it down and redistributing, OR upload a new version selecting only the new stores, OR upgrade to Unlimited

SOCIAL PLATFORM DISTRIBUTION (UPDATED MAY 2025):
As of May 23, 2025, free distribution to Social Platforms (Facebook/Instagram, TikTok, YouTube Music, YouTube Content ID, CapCut) is no longer available.
- Social Platform-only releases were taken down unless also sent to at least one Digital Store
- To distribute to Social Platforms only: pay upfront or use an Unlimited Plan
- Artists can still choose to distribute to Social Platforms only — it just requires payment


== STREAMING FRAUD & PENALTIES ==

WHAT IS STREAMING FRAUD?
Streaming fraud (also: streaming manipulation, artificial streaming, abnormal streaming activity) is artificially inflating views, streams, follows, and sales using bots, scripts, or paid promotion services. This includes paying companies that promise more streams, followers, playlist placements, or exposure.

CONSEQUENCES — SEVERE:
- Your music will be permanently removed from all stores (cannot be redistributed)
- Revenue from fraudulent streams will not be paid to you
- TuneCore may close your account entirely and restrict your ability to withdraw funds
- Artists are responsible even for third-party fraud committed on their behalf by a promotion service

SPOTIFY PENALTY (effective April 2024):
Spotify charges a €10 per-track per-month fee for tracks identified as having high artificial streaming. This fee is charged monthly per track per fraud report. TuneCore passes this to the account holder in local currency, charged to account balance or payment method on file.
- Can be charged multiple months in a row for the same track
- If 5 tracks flagged in one month = €50 charged that month
- Spotify also removes artificial streams from official sales reports and listener-facing play counts

STREAMING FRAUD NOTIFICATION:
If you receive a streaming fraud notification from TuneCore, your content has NOT been taken down yet — this is an early warning. However, continued fraudulent activity will lead to removal. Review your streaming data, verify your promotion services comply with platform rules, and report suspicious activity to the platform.

GOLDEN RULE: Never pay for services that promise to boost your streams, followers, or playlist placement.


== YOUTUBE CONTENT ID — ELIGIBILITY ==

ELIGIBLE tracks for Content ID:
- Tracks using only your own material for which you have exclusive rights
- Tracks using third-party material for which you have exclusive rights

INELIGIBLE tracks for Content ID (cannot be submitted):
- Contain instrumental content previously used in other tracks (samples, beats — even if exclusively licensed to you)
- Contain ambient sound effects (nature sounds), production loops, or audio library samples (GarageBand, Musicbed, AudioJungle)
- Meditation, yoga, or sleep music
- Contain 10+ consecutive seconds of spoken word
- Public domain clips or speeches
- Karaoke, soundalike, or tribute cover versions
- Covers of traditional or public domain songs (children's music, classical, holiday songs, religious songs/hymns)
- Part of a compilation, Various Artists release, DJ set, or continuous mix
- Previously exclusively licensed to a third party
- Already monetized on YouTube by another distributor
- Video game, TV, or film soundtracks (unless you are the publisher)
- Created specifically to exploit Content ID (e.g. end card music)

⚠️ Violations of eligibility guidelines may lead to permanent blocking from Content ID service.

HOW CONTENT ID WORKS IN PRACTICE:
- Tracks go through a vetting process before submission to YouTube
- YouTube creates a digital fingerprint of eligible tracks
- Scans all YouTube videos — can take up to 1 month for initial scan; new uploads matched instantly
- TuneCore collects 80% of revenue and deposits monthly into your TuneCore account
- Claims are NOT copyright strikes; they do NOT negatively affect channel standing
- You can whitelist your own YouTube channel to prevent TuneCore from monetizing your own videos
- Whitelisting your channel still allows monetization of all OTHER channels using your music

YOUTUBE MUSIC — ART TRACKS:
YouTube Music automatically creates "Art Tracks" from your audio + cover art when distributed. 1 billion monthly users. Free and premium tiers.
IMPORTANT: You must distribute to YouTube Music to distribute to YouTube Content ID.
Both are distributed via Social Platforms in the TuneCore Store Manager.


== TUNECORE ACCELERATOR ==

TuneCore Accelerator is TuneCore's artist development and promotion platform. Free to opt in for all TuneCore artists.

PROGRAMS INCLUDED:
1. Spotify Discovery Mode — Automated track selection for Spotify's personalized radio and autoplay
2. YouTube Creator Music — License tracks for use in YouTube creator videos
3. Early Access to New Features — Test new tools on Meta, TikTok, YouTube, Deezer, Spotify before most artists
4. Advanced Analytics — Track performance via the TuneCore Accelerator Dashboard

COST:
- Free to opt in; NO upfront cost
- 20% commission applies ONLY on streams directly generated through active Accelerator programs
- No lift = nothing to split; no risk, no cost

SPOTIFY DISCOVERY MODE (within Accelerator):
- Spotify charges 30% commission on Discovery Mode streams; TuneCore optimizes this to a 20% commission for the artist
- Track selection is automated using proprietary catalog technology — no manual selection needed
- Activation not guaranteed; tracks evaluated based on performance signals and platform criteria
- Commission applies ONLY to Discovery Mode context streams; all other streams remain commission-free
- Tracks are selected and deselected monthly based on performance
- Dashboard: TuneCore dashboard → Money & Analytics → TC Accelerator

OPT-IN / OPT-OUT:
- All TuneCore artists eligible; opt in via TuneCore Dashboard (Accelerator section → "Learn More")
- Cannot opt in/out of individual programs — it's all or nothing
- Opt out anytime; 24-hour lockout before you can opt back in
- If tracks were already selected for next month's campaign when you opt out, they run that month; exclusion starts the month after


== COMPANY BACKGROUND ==

TUNECORE & THE BELIEVE GROUP:
TuneCore is owned by Believe Group (French digital music company). In March 2023, Believe acquired Sentric Music, a global independent music publishing platform with offices in Europe and the US. Sentric Music has been powering TuneCore's Publishing Administration since 2018.

Key facts:
- TuneCore Publishing Administration has paid over $100 million to songwriters
- 23% of TuneCore subscribers benefit from Sentric Music's publishing solution
- Sentric is registered with 70+ collection societies in 200+ territories, representing 4M+ songs and 400K+ songwriters

BELIEVE GROUP BENEFITS FOR TUNECORE ARTISTS:
- Best-in-class streaming royalty rates (better than DIY, equal to or better than Indie sector)
- Spotify Marquee + Showcase ads at 50% bonus credit (~$3M in direct savings in 2024)
- Spotify Discovery Mode with above-industry-standard contractual terms via TuneCore Accelerator
- Top-tier preferred status on all major DSPs
- Access to innovative DSP programs including YouTube GenAI pilot and Style Transfer Carousel
- Best-in-class data access

GENAI MUSIC POLICY:
TuneCore distributes music created using GenAI tools ONLY where the underlying models rely on fully licensed datasets. This applies even if GenAI was used at any stage of music creation.
4 core principles: Consent, Control, Compensation, Transparency.
TuneCore does NOT maintain a list of approved GenAI tools — artists are responsible for verifying a tool's training data is properly licensed.

AI & DATA PROTECTION PROGRAM:
An invite-only program allowing artists to shape the future of AI/ML in music. If not invited, keep an eye on communications from TuneCore. Currently requires English language. No guaranteed access to specific opportunities; compensation varies by opportunity.


== TAKING DOWN MUSIC & REINSTATING ==

HOW TO TAKE DOWN A RELEASE:
Dashboard → Manage Subscriptions → find release → "Takedown All Stores" (immediate) OR "Cancel Renewal" (at end of period)
- Cannot take down individual tracks — must take down the entire album
- Most stores process within 2–3 business days; some take up to 3 weeks
- Cancellation requests must be submitted BEFORE the renewal date to avoid being charged

REINSTATING A TAKEN-DOWN RELEASE:
- Contact TuneCore Artist Support with the UPCs of releases you want reinstated
- Note: Once a release is taken down due to non-renewal, a new release (with a new UPC) may be required if the grace period has ended



== ACCOUNT — PAYMENTS & FINANCES ==

PAYMENT METHODS ACCEPTED BY TUNECORE:
Credit/debit cards: Visa, Mastercard, American Express, Discover
Digital wallets: PayPal, Apple Pay, Google Pay
TuneCore Balance
Local payment methods: GoPay / Dana (Indonesia), Gcash (Philippines), MoMo (Vietnam), iDEAL (Netherlands), China Union Pay (Global), Klarna (select territories with purchase limits)

SELF-BILLING (Non-US Artists):
Non-US clients must opt in to self-billing to withdraw revenue. Self-billing means TuneCore automatically generates withdrawal invoices on your behalf — you don't need to manually create invoices. Your withdrawal process stays the same; only the invoice generation changes. Required even if you are not in a VAT-compliant country.

PUBLISHING VS DISTRIBUTION ROYALTIES — WHY PUBLISHING IS LESS:
The ratio between distribution and publishing revenue is NOT 1:1. For every $100 of revenue on an interactive streaming service, the approximate breakdown is:
- $30.00 stays with the digital service provider (DSP)
- $59.50 paid to the sound recording (collected by TuneCore Distribution)
- $10.50 paid to the musical composition, split as:
  - $2.625 performance royalty — writer's share (paid by PRO directly to you)
  - $2.625 performance royalty — publisher's share (collected by TuneCore Publishing)
  - $5.25 mechanical royalty (collected by TuneCore Publishing)
This means approximately 10.5% of a streaming service's revenue goes to the composition — which is why publishing royalties appear much smaller than distribution royalties. Rates vary by territory.

REVENUE PAYMENT TIMING COMPARISON:
- Distribution revenue: stores report on a 2-month delay (January streams → March payment)
- Publishing revenue: paid quarterly, 45 days after each quarter end; first payment 3–18 months after registration depending on territory


== SPOTIFY — ARTIST TOOLS ==

SPOTIFY FOR ARTISTS:
Claim and verify your Spotify artist profile via TuneCore (Dashboard → "Spotify For Artists" box). Benefits: manage profile, use Artist Pick to highlight songs/playlists/tours, edit bio, upload photos, pitch singles to Spotify playlist editors, access listener statistics, promote shows.
⚠️ Log out of your personal Spotify account BEFORE claiming your page through TuneCore.
Can only claim once your release is live and playable on Spotify. Only Primary Artists, Featured, and Remixer roles with live content appear in the dropdown.

RELEASE APPEARING ON WRONG ARTIST PAGE (MAPPING ERROR):
This is called a "mapping error" — the DSP's auto-formatting placed your release on the wrong artist page. Solutions:
- Wrong release on YOUR Spotify page: Log in to Spotify for Artists → artists.spotify.com/c/content-mismatch → "Another artist's release is on my profile." Takes up to 15 business days.
- YOUR release on someone else's Spotify page: Same form → "Some of my music is on another artist's profile."
- Wrong Apple Music page: TuneCore Dashboard → My Artists → select artist → Releases - Artist IDs → Manage → choose your situation.
- Other stores: Contact TuneCore Artist Support with your TuneCore email, UPC, store names, correct artist page links, and wrong profile links. Most stores: 1–2 weeks. Amazon Music: 3–5 weeks.

RELEASE SHOWING IN SPOTIFY PLAYER AFTER TAKEDOWN:
If a taken-down release appears in your Spotify player, it is showing a LOCAL audio file on your computer — not a live store release. Spotify displays local files alongside store releases. A musical note icon appears next to local files. This is NOT an error with the takedown.


== TAKING DOWN & REINSTATING MUSIC ==

TAKEDOWNS ARE PERMANENT:
Once a release is taken down from stores, it CANNOT be reinstated — stores treat it as a final removal. To get your music back in stores, you must create a BRAND NEW release with a new UPC. You CAN reuse previous ISRCs as long as the audio file and track details are identical.

HOW TO TAKE DOWN:
Dashboard → Manage Subscriptions → find release → "Takedown All Stores" (immediate) OR "Cancel Renewal" (at end of renewal period). Submit at bottom of page.
Note: Cannot take down individual tracks — must take down the entire album.
Processing time: 2–3 business days for most stores; up to 3 weeks for some.
Cancellation requests must be submitted BEFORE the renewal date to avoid being charged for another year.


== WHY TUNECORE ==

Founded on the philosophy: "Sell your music, not your soul." Artists used include Drake, Jay-Z, Nine Inch Nails, Ziggy Marley, Keith Richards, Cheap Trick, Moby, Public Enemy, and more.

Key differentiators:
- Artists keep 100% of distribution revenue (flat annual fee, not a royalty split)
- Advanced sales reporting: filter by release, store, date
- Fast delivery: iTunes typically 24–72 hours
- 150+ global stores and streaming platforms
- TuneCore Accelerator: promotional tools powered by Believe Group's DSP relationships
- Publishing Administration: $100M+ paid to songwriters
- Artist Support: responds by next business day
- Part of Believe Group: preferred status on all major DSPs



== TAXES & PAYONEER ==

TAX FORMS — WHICH TO USE:
- U.S. persons (individuals or businesses): Submit a W-9 through Payoneer
  - Individual/Sole Proprietor: use your SSN (XXX-XX-XXXX)
  - Business (LLC, Corporation, Trust): use your EIN (XX-XXXXXXX)
- Non-U.S. persons: Submit a W-8 form (W-8BEN for individuals, etc.) — you can use a foreign tax ID instead of a U.S. ITIN
- Use the wizard on Payoneer's tax form page to determine which form applies to you
- TuneCore cannot provide tax advice — consult your local tax advisor or irs.gov

SUBMITTING TAX FORMS — PAYONEER:
All W-9 forms must be submitted through Payoneer (not directly to TuneCore). W-9s sent directly to TuneCore are NOT accepted.
To submit: Log in to Payoneer → Activity tab → Tax Form → Pending → Submit
To edit: Payoneer → Profile icon → Settings → Tax Forms → select form → Edit

BACKUP WITHHOLDING:
If no valid W-9 is on file, TuneCore withholds 24% of all U.S.-source royalties. Withholding applies until a valid W-9 is submitted via Payoneer. You can reclaim withheld amounts as a tax credit on your income tax return. TuneCore cannot reclaim or redeposit withheld funds.
Only one W-9 per TuneCore account (even if multiple artists use the account).

1099-MISC FORMS:
U.S. persons who earned at least $5 in royalties receive a 1099-MISC from TuneCore by January 31 each year, delivered digitally from Track1099 and by mail.
If you use both distribution and Publishing Administration: you receive two separate 1099s.
S-Corps do not receive 1099s.
To request a copy of a past 1099: contact TuneCore support from your registered email (allow ~3 weeks for digital copies).

PAYONEER & RUSSIA:
Since December 16, 2022, Payoneer no longer serves clients based in Russia or clients with Russian bank accounts. TuneCore accounts of Russian-based artists have NOT been closed and releases remain live, but withdrawals to Payoneer are no longer possible. Revenue continues to accrue in TuneCore accounts.


== CREATING YOUR TUNECORE ACCOUNT ==

HOW TO CREATE AN ACCOUNT:
1. Go to tunecore.com → click "Sign Up" (top right)
2. Enter name, country, email, and create a password → click "Create my free account"
3. Verify your email via the link sent to you
One email address per account. One account can manage multiple artists.


== RELEASE STATUS GUIDE ==

Statuses shown in your TuneCore Discography:
- Submitted for Review: Successfully uploaded and awaiting review
- Needs Action: Content Review denied it; check email or Release Tracker for what to fix; reply to the email after correcting it
- Approved & Sent / Sent: Delivered to stores (may show "Sent" even before going live if iTunes is not selected)
- Live: Successfully sent to iTunes and other selected stores; future release dates will not be live until that date
- Incomplete / Ready to Go: Not yet checked out, or changes were requested and release was unfinalized
- Taken Down: Removed from all stores
- Denied: Content Review denied; check the email for details


== RELEASE TRACKER ==

TuneCore's Release Tracker (launched for most releases) shows where your release is in the distribution process in real time. Find it: Discography → click the colorful status bubble with the arrow on any release.
Features: shareable graphic you can download and post (with animated background and audio snippet option). Download goes to Files/Downloads folder on mobile (not camera roll).
Releases without a tracker: Social Platform-only releases, New Artist Plan releases (discontinued), or releases not yet submitted.


== FACEBOOK / INSTAGRAM / META ==

WHAT IT IS:
Distributing to Facebook/Instagram allows creators to use your music in their Facebook and Instagram content (Stories, Reels, videos). TuneCore claims and monetizes videos that use your music. Revenue is reported under "Meta" in your Sales Reports. Facebook pays out quarterly.

IMPORTANT: All tracks on a release must be eligible for the entire release to appear on Facebook/Instagram. If even one track is ineligible, the whole release is excluded.

ELIGIBILITY REQUIREMENTS (same as YouTube Content ID):
You can only submit tracks where you have exclusive rights. Cannot submit: samples, beats, ambient sounds, production loops, audio library samples, covers, remixes, spoken word (10+ sec), public domain material, karaoke/soundalike versions, traditional/holiday songs, compilations, DJ sets, tracks previously licensed exclusively to a third party, tracks already monetized on Facebook through another distributor, or music created solely to exploit the service.

Go-live time: Most releases live within 1 week, up to 2 weeks.

FINDING YOUR TRACK ON INSTAGRAM:
Search format: "ISRC:XXXX12345678" (replace with your track's ISRC). Once you find it, save it to Favorites.

SALES REPORTING:
Use the "World Wide" country filter PLUS all country-specific filters to see total Meta earnings (World Wide alone only shows countries without individual filters).


== YOUTUBE SHORTS ==

YouTube Shorts (short-form videos up to 60 seconds) uses YouTube Content ID. When you distribute to YouTube Content ID, your music automatically becomes available for Shorts creators. You cannot opt into Shorts without also opting into YouTube Content ID. Available to creators in 100+ countries.


== LYRICS ==

DISTRIBUTING LYRICS:
TuneCore distributes lyrics directly to iTunes/Apple Music only (text only — not synchronized/time-stamped).
For Spotify and Instagram lyrics: use a third-party service like MusixMatch.
For Instagram Stories lyrics: sign up for Facebook/Instagram Monetization AND get verified on MusixMatch to enable lyric stickers in Stories.


== DOLBY ATMOS / SPATIAL AUDIO ==

TuneCore delivers Dolby Atmos to Apple Music (played back as Spatial Audio with dynamic head-tracking on compatible Apple devices).
Cost: $16.99 USD per track (added during upload). Cannot be added to existing releases at this time.
File specs: BWF ADM format, 24-bit LPCM audio at 48kHz, all tracks at same frame rate, max -18 LKFS loudness, max -1 dB TP true-peak. Auto-generated/algorithmic Dolby Atmos from stereo masters is NOT accepted by Apple Music.


== SOUNDTRACKS & MUSICAL THEATER ==

Soundtracks and Musical Theater releases CAN be distributed through TuneCore.
⚠️ Soundtracks are NOT eligible for Meta (Facebook/Instagram) or YouTube Content ID monetization.

TV/Film/Video Game Soundtracks: Title must include version info (e.g. "Title (Original Motion Picture Soundtrack)", "Title (Music from the Original TV Series)").
Musical Theater: Full cast recordings must include production info (e.g. "Title (Original Broadway Cast Recording)"). Do NOT use "Original" if it's not from the original staging.
Composer credit: For instrumental scores, composer must be credited as Primary Artist AND Composer at album level. "Various Artists" is NOT acceptable for scores.


== THE MECHANICAL LICENSING COLLECTIVE (MLC) ==

The MLC is a U.S. nonprofit designated by the Copyright Office to collect digital mechanical royalties under the Music Modernization Act of 2018. Fully funded by DSPs — no cost to songwriters.

WHAT MLC COLLECTS: U.S. mechanical royalties from Spotify, Apple Music, Amazon Music, Tidal, iTunes downloads only.

WHAT MLC DOES NOT COLLECT: YouTube, TikTok, Facebook micro-sync royalties; global mechanical royalties; performance royalties (PROs collect those); physical sales; lyric royalties; print royalties; sync fees.

TuneCore Publishing clients do NOT need to register with the MLC separately — TuneCore handles MLC registration as part of its global registration across hundreds of societies.


== PUBLISHING ADMINISTRATION — SETUP GUIDE ==

HOW TO SUBMIT SONGS:
Publishing Dashboard → Songs → "Add a new song" OR use the Song Suggestion tool (auto-imports metadata from TuneCore distributed releases and Spotify for Artists).
Add your share % and co-writer shares. Click "Add Writer" after selecting each writer. Tick the "right to collect" box. Click "Add Song."
Songs are NOT automatically registered when you distribute — you must actively submit them with share information.
100% of writer shares must be accounted for when submitting.

SONGS NOT DISTRIBUTED THROUGH TUNECORE:
Publishing Dashboard → "Add a New Song" → search by Spotify or enter metadata manually → add shares → add performing artist → "Add Song."

COLLECTING FOR CO-WRITERS:
You can collect for other songwriters ONLY with their explicit permission. Doing so without permission is a Terms of Service violation and may result in account termination and retraction of royalties.

MULTIPLE RECORDINGS OF THE SAME COMPOSITION:
Publishing Dashboard → Songs → click drop-down arrow → "Add New Recording" → search Spotify or enter metadata (title, artist, ISRC, label, release date) → "Add Recording."

PUBLISHING COST (UPDATED):
$75 one-time sign-up fee. 20% commission on royalties collected. 50% commission on sync licenses.

SYNC IS OPTIONAL:
If you opt into sync, TuneCore's sync team pitches and licenses your songs at 50% commission. If you opt out, you can work with other sync agents while still collecting publishing royalties.

DASHBOARD FEATURES (Updated Publishing Dashboard powered by Sentric):
- Quarterly Royalty Statements
- Advanced analytics on song performance
- Collect royalties for yourself and other songwriters you're authorized to represent
- Connect Spotify for Artists for seamless song management

IDENTITY VERIFICATION (iDenfy):
TuneCore requires ID verification for Publishing Administration via iDenfy (third-party). You'll need a valid ID with your photo. Verification takes 3–20 minutes on average. If you fail, contact Publishing Support to request a retry. Only one verification needed even across multiple artists you manage.


== TOOLS & INTEGRATIONS ==

SESSION STUDIO:
A third-party DAW integration that lets you send release info (audio files + metadata) directly from Session Studio to TuneCore. Go to sessionstudio.com → connect your TuneCore account → add songs → Create Release → TuneCore. After sending, you still need to complete: Spotify/Apple Artist IDs, genre, contributor roles, copyright, explicit info, lyrics language, cover art, and TikTok Clip Start Time.

LINKSHARE (IN-APP, FREE):
A free built-in feature (separate from TuneCore Social Pro's linkShare) that creates a shareable page with links to your single across major stores. Only available for singles (not albums) that are live in at least one LinkShare store. Stores included: Amazon Music, Apple Music, Deezer, iTunes, Napster, Pandora, Spotify, TIDAL, YouTube Music. No analytics available. Not customizable. Accessible via the link icon on your release's cover art thumbnail in your Dashboard or Discography.

SUPPORTED BROWSERS:
Desktop: Firefox v78+, Safari v12.1.3+, Chrome 80+, Microsoft Edge v19+
Mobile iOS: iOS 11 Safari v14+
Mobile Android: Android v8+
If having upload issues: clear cookies/cache, close browser, reopen and retry. Upload from computer only (not mobile or tablet).


== MARKETING & PROMOTION ==

LEGITIMATE PROMOTION STRATEGIES:
- Pitch directly to Spotify playlist editors via Spotify for Artists (official pitching, free)
- Use Spotify Campaign Kit or audio ads
- Use TuneCore Accelerator for Spotify Discovery Mode (automated, free to opt in)
- Radio Airplay and Feature.fm (special offers for TuneCore artists for internet radio/Deezer streams)
- Build a website (discounted Bandzoogle plans for TuneCore artists)
- Social media presence; use TuneCore Social for multi-platform management
- Email fans with direct store links to drive streams and downloads
- Share your Release Tracker graphic on social media at launch

⚠️ NEVER pay for services promising streams, followers, or playlist placement — this is streaming fraud and can result in permanent removal from stores and account closure.



== GETTING STORE LINKS & FINDING YOUR RELEASE ==

STORE LINKS IN YOUR TUNECORE ACCOUNT:
TuneCore automatically creates iTunes, Apple Music, and Spotify links to your release. Find them in your release dashboard → select your release → "Store Links" section (right side of page). These appear once your release shows "Live."
iTunes link tip: If an iTunes link only opens Apple Music, add ?ls=1&app=itunes to the end of the Apple Music URL to force the iTunes download store link.
To find your release in other stores: use TuneCore's LinkShare feature, or search by release title + artist name directly in each store.
To check a foreign iTunes territory: open the iTunes application → click the flag at the bottom right → select the territory → search your release.

HOW DO I RECEIVE MY RINGTONE IN ITUNES?
Ringtones are only distributed to iTunes on iPhone. After distribution, search for your ringtone in the iTunes Store under "Tones" (not Music). Processing can take a few days after your release status shows "Live."


== YOUTUBE OFFICIAL ARTIST CHANNEL (OAC) ==

A YouTube OAC merges your Topic Channel(s) and Vevo channel (if applicable) into your primary owned-and-operated (O&O) artist channel. Unlocks: Discography section, Official Music Videos section, featured video slot, and merchandise retailer links.

ELIGIBILITY REQUIREMENTS (must meet ALL):
- Must be on an Unlimited Plan
- Artist must be credited as Main/Primary Artist at the release level (Featured artists are not eligible)
- O&O channel represents only one artist or band (no label channels)
- At least 15 subscribers (50 for tunecore.in users)
- At least 1 official Art Track live on YouTube distributed by TuneCore
- No policy violations on your YouTube channel
- O&O channel must have existing public video uploads (at least 1 music video preferred)

HOW TO CLAIM:
TuneCore Dashboard → scroll down to "Your Artist Accounts" → click "YouTube Official Artist Channel" box → select your artist → Claim → authenticate your Google account → confirm. Processing takes 2–3 weeks (may take longer due to YouTube's processing times).


== ADVANCED PUBLISHING — ROYALTIES & LEGAL ==

BLACK BOX ROYALTIES:
"Black box" royalties are unclaimed/unallocated royalties that collection societies cannot match to the correct songwriter or publisher — due to bad data or because the songwriter was never registered with that society. Unmatched royalties are often redistributed to local publishers based on market share, meaning your money can be permanently lost to others. The solution: register globally with TuneCore Publishing, which covers hundreds of collection societies worldwide.

YOUTUBE ACCRUED ROYALTIES:
TuneCore enters an annual settlement with YouTube to claim unclaimed composition royalties from the prior two years. This covers US-based YouTube mechanical reproduction royalties only. If you don't claim by the annual deadline, those royalties may be permanently lost (unless you pursue legal action). Payouts are typically included in the November quarterly distribution after each deadline. TuneCore handles this automatically for all Publishing Administration clients who have registered their songs — no action required if your songs are registered.

CLAIMING DIGITAL US MECHANICAL ROYALTIES (MLC):
TuneCore works directly with The MLC to identify and claim US digital mechanical royalties historically not matched to rightsholders. Once songs are registered in your Publishing Dashboard, royalties identified at The MLC can take 6–9 months to appear in your account. TuneCore handles all MLC registration — you don't need to register directly.

PUBLIC DOMAIN WORKS:
You can collect songwriter royalties on public domain works IF you have created a new arrangement. In the US, works published before 1926 are in the public domain. As arranger (not composer/author), you can register the new arrangement. Note: public domain works generate fewer royalties — BMI pays 20% of normal value, ASCAP pays 10%. A work may be in public domain in the US but still protected in other countries. Always verify which specific version/arrangement is in the public domain.

THE MUSIC MODERNIZATION ACT (2018):
Signed October 28, 2018 — overhauled US copyright law to better support songwriters and artists. Three key components:
1. Musical Works Modernization Act (Title I): Created a blanket license for interactive streaming services; established The MLC to collect and distribute digital mechanical royalties. Makes royalty collection more efficient for creators and easier for music services to obtain licenses.
2. Classics Protection and Access Act (Title II): Created federal rights for owners of sound recordings made before February 15, 1972 (previously only covered by state law).
3. Allocation for Music Producers Act — AMP Act (Title III): Made it easier for producers, mixers, and engineers to collect satellite/online radio royalties via a Letter of Direction to SoundExchange.


== COPYRIGHT REGISTRATION ==

TuneCore does NOT register your songs with the Copyright Office.
Your music is automatically protected by copyright law as soon as it is created in a fixed form (recorded or written down).
For formal registration with the US Copyright Office (copyright.gov):
- PA form (Performing Arts): used to register a musical composition (lyrics and music)
- SR form (Sound Recording): used to register the master/sound recording only
Registration creates a public record and gives you stronger legal standing and better damage compensation if infringement occurs.



== HATE CONTENT POLICY ==

TuneCore will NOT distribute content that:
1. Harasses or denigrates an individual or group of people
2. Promotes racism, bigotry, hatred, or physical harm against any group or individual
3. Is abusive, defamatory, pornographic, threatening, or obscene
4. Is intended to promote commission of any illegal act

TuneCore reserves the right to refuse distribution or take down any content violating this policy at any time without prior notice. All major store partners (Apple Music, Spotify, TIDAL, YouTube Music, Pandora, iHeartRadio) enforce similar policies.


== REFUND POLICY ==

All TuneCore sales are final. No refunds are issued. Contact TuneCore Artist Support for questions or concerns.


== TUNECORE SYNC — FULL DETAILS ==

TuneCore Sync is available to TuneCore Publishing Administration clients who also distribute through TuneCore. It's a creative pitching and licensing service where TuneCore's dedicated sync teams (based in Los Angeles, New York, London, Paris, and beyond) pitch your music to music supervisors and creatives for use in movies, TV, commercials, video games, trailers, and more.

PRE-APPROVAL MODEL: By opting into TuneCore Sync, you pre-approve the team to pitch and license your music. You do NOT need to approve each individual sync opportunity. Exception: sensitive/controversial uses (political ads, explicit scenes, drug use) — the team will reach out for your approval as a courtesy.

PITCHING YOUR OWN MUSIC: Encouraged! But as soon as a music supervisor shows interest and starts discussing terms, loop in the TuneCore sync team immediately. Do NOT sign anything without them.

PAYMENT SCHEDULE: Sync fees appear in Quarterly Publishing Royalty Statements, posted 45 days after each quarter (May 15, Aug 15, Nov 15, Feb 15). Fees are included in the quarter TuneCore RECEIVES payment from the production — not when the sync airs.

KEY FACTORS IN SYNC SELECTION:
1. Creative fit — does the music match the project's needs?
2. Cost — recognizable hits are more expensive; indie/back catalog is more affordable
3. Rights — 100% of publishing rights must be accounted for; even 1% holdout can block the entire sync
4. Sonic quality — high-quality WAV files are required; poor production quality hurts chances

BEST PRACTICES FOR SYNC:
- Always have high-res WAV files ready
- Have instrumental versions (many projects need instrumental-only; also needed if explicit lyrics)
- Have clean versions of explicit tracks
- Maintain a strong online presence (music supervisors research artists)
- Be patient — sync opportunities come in waves
- If your beat contains a cleared sample: furnish documentation from the original publisher/label

OPT-OUT: Opt out anytime from your Publishing Dashboard. 45-day notice rule applies: if you opt out 45+ days before quarter end → termination effective end of current quarter. If less than 45 days → termination effective end of the following quarter.


== PRO MEMBERSHIP & TUNECORE PUBLISHING ==

CAN I SIGN UP FOR TUNECORE PUBLISHING IF ALREADY A PRO MEMBER?
Yes. TuneCore Publishing Administration complements your PRO membership — it does NOT replace it. Your PRO (ASCAP, BMI, SESAC, etc.) collects performance royalties. TuneCore Publishing collects mechanical royalties, the publisher's share of performance royalties, and other royalty streams your PRO does not collect.

WILL I STILL RECEIVE PRO STATEMENTS?
Yes. If you are a TuneCore Publishing Administration client, you will continue to receive your statements from your PRO. TuneCore and your PRO work together — TuneCore registers your works with your PRO on your behalf as well as hundreds of other societies worldwide.



== MAKING CHANGES TO YOUR RELEASE ==

WHAT CANNOT BE CHANGED AFTER DISTRIBUTION (LOCKED):
- Adding, deleting, or reordering songs
- Changing the UPC or ISRC
- Changing the Original Release Date

For any of these, you must take down the release and redistribute as a new release (new UPC, can reuse ISRCs if audio is identical).

FOR ALL OTHER CHANGES — 2 OPTIONS:
Option 1 (Recommended): Take down and redistribute. Guaranteed to update in all stores. Permanent — cannot be reversed. You pay for distribution again (no refund on the original). Release loses any store reviews, comments, and playlist placements associated with the original UPC.

Option 2: Edit existing release directly. NOT recommended — TuneCore cannot guarantee all stores will apply the change. Spotify, iTunes, Amazon Music: ~2 weeks. Other stores: weeks to months, or possibly never.

SELF-EDITABLE FIELDS (from your Discography, pencil icon):
- TikTok Start Time
- Songwriter
- Language of Lyrics (excluding Chinese, Russian, Arabic, Hebrew, Bulgarian, Ukrainian — Cyrillic language rules)
- Contributors (excluding Primary Artist, Featured Artist, Remixer, Producer)


== CREATING A NEW RELEASE — STEP BY STEP ==

1. Dashboard → Releases tab → Discography → choose Album, Single, or Ringtone
2. Fill in release information → Save release and add songs
3. Add songs: title, songwriter info, contributors, cover song info, lyrics, ISRC (free if you don't have one)
4. Upload audio files (must meet audio requirements — WAV preferred)
5. Upload artwork (cover art requirements apply)
6. Select digital stores and social platforms → Save
7. Complete checklist (checkmark next to every item)
8. Checkout:
   - Unlimited Plan: click "Distribute with Plan" → "Release Music"
   - Pay-Per-Release: select 1yr/2yr/5yr subscription → "Add to Cart" → "Proceed to Checkout"

Note: TuneCore only distributes ringtones to the iTunes store on iPhone.


== RESETTING YOUR PASSWORD ==

1. Go to tunecore.com → Login (top right) → "I forgot my password"
2. Enter your account email → click "Request Password Reset"
3. Check your email for reset instructions

Password requirements: at least 8 characters, 1 uppercase letter, 1 number, 1 special character (@$!%*?&)


== RELEASE RENEWALS (PAY-PER-RELEASE) ==

For pay-per-release accounts only (Unlimited Plan renewals are covered separately):
- Flat fee per release, on a 1-year, 2-year, or 5-year subscription basis
- All accounts are auto-enrolled in auto-renewals by default
- Email reminder sent 28 days before renewal due date
- If renewal fails: email every 7 days for 35 days → after 42 days, release is taken down (grace period ends)
- Payment: charged to saved credit card or PayPal automatically; if that fails, tries other saved methods

To turn off auto-renewal: contact Artist Support (submit a request from your registered email address). Or manually renew: Account Settings → Subscriptions → "Pay now" next to the renewal.

Note: Distribution credits cannot be used to pay for renewals.


== YOUTUBE CONTENT ID — ADDITIONAL Q&A ==

DO I NEED A YOUTUBE CHANNEL TO USE CONTENT ID?
No. TuneCore can monetize the use of your audio in videos created by other people even without your own YouTube channel.

CAN YOUTUBE MONETIZE COMPOSITION RIGHTS?
No. YouTube Content ID only monetizes your sound recording rights (the master). To monetize your composition rights (songwriting royalties), you need TuneCore Publishing Administration.

HOW ARE OTHER PEOPLE'S VIDEOS IMPACTED?
When your song is opted into Content ID, YouTube places a claim (NOT a copyright strike) on videos using your music. Claims do not negatively affect a channel's standing — they simply enable monetization on your behalf. Video creators cannot prevent YouTube from placing ads on a monetized video. The only way to avoid ads is to take the video down.

CAN I EXCLUDE A SPECIFIC VIDEO FROM MONETIZATION?
You can whitelist your own YouTube channel (to prevent TuneCore from monetizing your own videos). You cannot exclude individual third-party videos. Only one channel whitelist is allowed.


== PUBLISHING ADMINISTRATION — COPYRIGHT Q&A ==

WHAT IS THE DIFFERENCE BETWEEN A COMPOSITION AND A SOUND RECORDING?
A composition = the musical work itself — the lyrics and melody written by the songwriter/composer.
A sound recording (master) = the recording of a performance of that composition.
Example: Dolly Parton wrote "I Will Always Love You" (composition). Whitney Houston recorded a version (sound recording). Arista Records owns the sound recording; Dolly Parton owns the composition.

BY HIRING TUNECORE, DO I GIVE UP MY COPYRIGHTS?
No. You grant TuneCore exclusive administration rights for the duration of the agreement. You retain 100% ownership and control of your songs, music, and copyright.

CAN I SUBMIT A COMPOSITION I COVERED FOR PUBLISHING ADMIN?
No. You can only submit ORIGINAL compositions that you wrote. Covers are owned by the original songwriter and their publisher. You CAN submit co-written songs (submit your share only, providing co-writers' names and shares).

CAN I SUBMIT COMPOSITIONS I DID NOT WRITE?
No. Only original compositions you wrote (or co-wrote) are accepted for Publishing Administration.

DOES MY COMPOSITION GENERATE ROYALTIES WHEN STREAMED?
Yes. When your song is streamed on an interactive service (Spotify, Apple Music, YouTube, Tidal):
1. Performance royalty (writer's share) — collected by your PRO
2. Mechanical streaming royalty — collected by TuneCore Publishing Administration
3. Sound recording royalty — collected by TuneCore Distribution

When played on non-interactive/internet radio (Pandora, Sirius XM, TuneIn):
1. Performance royalty for the composition — collected by your PRO
2. Performance royalty for the sound recording — collected by SoundExchange (paid direct to artists)



== SWITCHING TO TUNECORE FROM ANOTHER DISTRIBUTOR ==

1. Contact your current distributor and request a takedown of your content.
2. You can re-upload and distribute through TuneCore even before the old release is fully removed from every store.
3. Expect a brief overlap or gap — each store processes removals and new deliveries on its own timeline.
4. UPC and ISRC reuse: You CAN reuse the same UPC and ISRCs if the release is exactly the same (same tracks, same order, same audio). You CANNOT reuse them if anything changed (tracks added/removed/reordered, audio re-recorded/remixed, titles changed).
5. If you need new codes, leave the fields blank — TuneCore generates UPCs and ISRCs for free.


== WHY HAS MY MUSIC BEEN REMOVED FROM STORES? ==

If you didn't request a takedown but your release disappeared from stores, the most likely cause is an expired renewal. Music must be renewed annually (pay-per-release accounts) or covered by an active Unlimited Plan to stay live.

TuneCore sends notification emails before your renewal date, then two more reminders after it passes, with a grace period to pay before removal. Once a release is removed, it is final and cannot be reversed — you must redistribute as a brand new release with a new UPC. ISRCs can be reused if the audio is identical.


== FENDER STUDIO PRO+ INTEGRATION ==

Fender Studio Pro+ is a DAW (Digital Audio Workstation) with a direct TuneCore integration — similar to Session Studio. You can produce, record, mix, and master, then send finished tracks directly to TuneCore.

HOW TO GET STARTED:
1. Connect your TuneCore account by clicking the TuneCore record logo in the top-left of Fender Studio Pro+
2. Authorize access to your TuneCore account
3. Upload songs with audio files and metadata
4. Click the TuneCore record logo → send release to TuneCore
5. Complete the release in your TuneCore account (cover art, store selection, contributor roles, explicit info, etc.)

WHAT TRANSFERS AUTOMATICALLY: Release Title, Primary Artist, Release Language, Song Title, Release Date, Audio files.

WHAT YOU MUST ADD IN TUNECORE: Spotify/Apple Artist IDs, contributor roles, copyright info, explicit flags, lyrics language, cover art, TikTok Clip Start Time (optional), store selection.

DISCOUNTS:
- Existing TuneCore artists: 25% off first year of Fender Studio Pro+ Hybrid Subscription
- Fender Studio Pro+ subscribers new to TuneCore: 50% off first year of Rising, Breakout, or Professional Unlimited Plan


== YOUTUBE — MONETIZABLE VIEWS ==

A "Monetizable View" on YouTube means a view that generates revenue from your sound recording. Two types:
1. Ad-supported view: An ad is placed on the video → that view generates revenue from the ad.
2. Premium subscription view: A YouTube Premium subscriber (no ads) watches the video → revenue is generated from their monthly subscription fee.

Not every view is monetizable. Whether a view has ads depends on: the viewer's territory, the per-viewer ad frequency limits, and various copyright policies.



== COMMON QUESTIONS ==

Q: Do I keep 100% of my royalties from distribution?
A: Yes — TuneCore charges a flat annual fee for distribution, not a percentage of your royalties. You keep 100% of revenue from sales and streams. (Publishing Administration is a separate service with its own commission structure: 20% on general royalties, 50% on sync licenses.)

Q: What is the difference between a Performer and a Songwriter?
A: A Performer (recording artist) earns money from sales/streams of the sound recording. A Songwriter/Composer earns publishing royalties from the underlying composition (melody and lyrics). These are separate royalty streams collected differently.

Q: What is SoundExchange?
A: SoundExchange is a U.S. non-profit that collects digital performance royalties for sound recordings from non-interactive digital radio (Pandora, SiriusXM, etc.). These are separate from PRO publishing royalties.

Q: What is a PRO?
A: A Performing Rights Organization (PRO) — such as ASCAP, BMI, SESAC in the U.S. — collects public performance royalties for songwriters when music is played on radio, TV, in businesses, or streamed. TuneCore Publishing Administration collects royalties your PRO may miss, like mechanical and foreign royalties.

Q: What is a CAE/IPI Number?
A: A unique identifier (Composer, Author, Publisher / Interested Parties Information) assigned to songwriters by PROs worldwide. Used to track and route royalty payments globally.

Q: Can I distribute cover songs?
A: Yes, but you need a mechanical license for the original composition first. TuneCore offers Cover Song Licensing via Royalty Solutions. Soundalike covers are not accepted. See cover song rules above.

Q: Can I set territory restrictions?
A: Yes — TuneCore allows artists to restrict distribution to specific territories/countries.

Q: What is the Store Automator?
A: TuneCore's Store Automator automatically adds newly partnered stores to your existing releases as TuneCore expands its network.

Q: Can I monetize my music on YouTube if I don't have a YouTube channel?
A: Yes — YouTube Content ID allows TuneCore to monetize your sound recordings on YouTube across all videos that use them, even without your own channel.

Q: How long until my publishing royalties arrive?
A: Typically 9–12 months for the first payment (12–18 months for foreign societies). After that, payouts happen quarterly, 45 days after each quarter ends.
`;

const SUGGESTED_QUESTIONS = [
  "How do I distribute my music with TuneCore?",
  "Do I keep 100% of my royalties?",
  "What's the difference between a Single, EP, and Album?",
  "How does TuneCore Publishing Administration work?",
  "When do I get paid?",
  "What are TuneCore's cover art requirements?",
  "How does YouTube Content ID work?",
  "Can I distribute cover songs?",
];

export default function App() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Welcome to the TuneCore Support Assistant. I have access to TuneCore's official Knowledge Base and can answer your questions about music distribution, publishing administration, YouTube Content ID, royalties, releases, and more. What would you like to know?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;

    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1500,
          system: PROJECT_CONTEXT,
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      const data = await res.json();

      // Extract text blocks from response (MCP tool results are handled server-side)
      const reply =
        data.content
          ?.filter((item) => item.type === "text")
          .map((item) => item.text)
          .join("\n") || "I couldn't process that. Please try again.";

      setMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch {
      setMessages([
        ...newMessages,
        { role: "assistant", content: "Connection error. Please try again." },
      ]);
    }
    setLoading(false);
  };

  const showSuggestions = messages.length === 1;

  return (
    <div
      style={{
        fontFamily:
          "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        background: "#1a1919",
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        color: "#e8e4dc",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          flexShrink: 0,
          borderBottom: "1px solid #2a2820",
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          background: "#0d0d0a",
        }}
      >
        <img src="/intuitio_logo_white.svg" height="28px" alt="Intuitio Labs" />
        <div style={{ width: "1px", height: "28px", background: "#2a2820", marginLeft: "4px" }} />
        <div>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "600",
              fontFamily: '"Neue Haas Grotesk", Helvetica, Arial, sans-serif',
              letterSpacing: "0.04em",
              color: "#ffffff",
            }}
          >
            TuneCore Support Assistant
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#7a7468",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 600,
              fontFamily:
                "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            IntuitioLabs · TuneCore Knowledge Base
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <div
            style={{
              fontSize: "11px",
              color: "#c9a84c",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              border: "1px solid #3a3420",
              padding: "4px 10px",
              borderRadius: "2px",
            }}
          >
            Support
          </div>
        </div>
      </div>

      {/* ── Chat messages (scrollable) ── */}
      <div
        className="chat-shell"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "24px 32px 16px",
          maxWidth: "780px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
          background: "#1a1919",
          borderLeft: "1px solid #262626",
          borderRight: "1px solid #262626",
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              marginBottom: "28px",
              display: "flex",
              flexDirection: "column",
              alignItems: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            {m.role === "assistant" && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#c9a84c",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                  paddingLeft: "2px",
                }}
              >
                TuneCore Support
              </div>
            )}
            <div
              style={{
                maxWidth: "88%",
                padding: m.role === "user" ? "12px 18px" : "20px 24px",
                background:
                  m.role === "user"
                    ? "linear-gradient(135deg, #1e1c14, #2a2618)"
                    : "#111108",
                border:
                  m.role === "user"
                    ? "1px solid #3a3420"
                    : "1px solid #1e1c14",
                borderRadius:
                  m.role === "user"
                    ? "12px 12px 2px 12px"
                    : "2px 12px 12px 12px",
                fontSize: "15px",
                lineHeight: "1.75",
                color: m.role === "user" ? "#d4cfc4" : "#e0dbd0",
              }}
              className={m.role === "assistant" ? "md-body" : ""}
              {...(m.role === "assistant"
                ? { dangerouslySetInnerHTML: { __html: renderMarkdown(m.content) } }
                : { children: m.content })}
            />
          </div>
        ))}

        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#7a7468",
              fontSize: "13px",
              paddingLeft: "2px",
            }}
          >
            <div style={{ display: "flex", gap: "4px" }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: "5px",
                    height: "5px",
                    background: "#c9a84c",
                    borderRadius: "50%",
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                    opacity: 0.6,
                  }}
                />
              ))}
            </div>
            Searching Knowledge Base...
          </div>
        )}

        {showSuggestions && !loading && (
          <div style={{ marginTop: "8px" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#5a5650",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                marginBottom: "14px",
              }}
            >
              Common questions
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  style={{
                    background: "transparent",
                    border: "1px solid #2a2820",
                    borderRadius: "20px",
                    padding: "7px 16px",
                    fontSize: "13px",
                    color: "#a09890",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = "#c9a84c";
                    e.target.style.color = "#c9a84c";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#2a2820";
                    e.target.style.color = "#a09890";
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input (fixed at bottom) ── */}
      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid #1e1c14",
          padding: "20px 32px",
          background: "#0d0d0a",
          maxWidth: "780px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about distribution, royalties, publishing, YouTube Content ID..."
            rows={1}
            style={{
              flex: 1,
              background: "#111108",
              border: "1px solid #2a2820",
              borderRadius: "8px",
              padding: "12px 16px",
              fontSize: "14px",
              color: "#e8e4dc",
              fontFamily: "inherit",
              resize: "none",
              outline: "none",
              lineHeight: "1.5",
              maxHeight: "120px",
              overflow: "auto",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#c9a84c")}
            onBlur={(e) => (e.target.style.borderColor = "#2a2820")}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            style={{
              background:
                input.trim() && !loading
                  ? "linear-gradient(135deg, #c9a84c, #a8881c)"
                  : "#1e1c14",
              border: "none",
              borderRadius: "8px",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: input.trim() && !loading ? "pointer" : "not-allowed",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke={input.trim() && !loading ? "#0a0a08" : "#3a3830"}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <div
          style={{
            marginTop: "10px",
            fontSize: "11px",
            color: "#3a3830",
            textAlign: "center",
            letterSpacing: "0.05em",
          }}
        >
          This assistant has access to TuneCore's official Knowledge Base · For
          further support visit{" "}
          <span style={{ color: "#5a5650" }}>support.tunecore.com</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        textarea::placeholder { color: #3a3830; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2820; border-radius: 2px; }

        @media (max-width: 640px) {
          .chat-shell {
            padding: 20px 16px;
            border-left: none;
            border-right: none;
          }
        }

        /* ── Markdown rendering ── */
        .md-body { color: #ffffff; }
        .md-body p { margin: 0 0 12px 0; color: #ffffff; }
        .md-body p:last-child { margin-bottom: 0; }
        .md-body h1, .md-body h2, .md-body h3 {
          color: #ffffff;
          font-weight: 700;
          margin: 18px 0 8px 0;
          line-height: 1.3;
        }
        .md-body h1 { font-size: 17px; }
        .md-body h2 { font-size: 15px; }
        .md-body h3 { font-size: 14px; letter-spacing: 0.03em; }
        .md-body ul, .md-body ol {
          margin: 8px 0 12px 0;
          padding-left: 22px;
          color: #ffffff;
        }
        .md-body li { margin-bottom: 2px; line-height: 1.6; color: #ffffff; }
        .md-body strong { color: #ffffff; font-weight: 700; }
        .md-body em { color: #e0dbd0; font-style: italic; }
        .md-body code {
          background: #1e1c14;
          border: 1px solid #2e2c1e;
          border-radius: 4px;
          padding: 1px 6px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          color: #ffffff;
        }
        .md-body pre {
          background: #1a180f;
          border: 1px solid #2e2c1e;
          border-radius: 6px;
          padding: 14px 16px;
          overflow-x: auto;
          margin: 10px 0;
        }
        .md-body pre code {
          background: none;
          border: none;
          padding: 0;
          font-size: 13px;
          color: #ffffff;
        }
        .md-body a {
          color: #c9a84c;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .md-body a:hover { color: #e0c070; }
        .md-body hr {
          border: none;
          border-top: 1px solid #2a2820;
          margin: 16px 0;
        }
      `}</style>
    </div>
  );
}
